import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, Navigate, useNavigate, useSearchParams, type NavigateFunction } from "react-router";
import { checkUsername, forgotPasswordSchema, loginSchema, passwordSchema, registerSchema, type Me, type SignInCodeRequired } from "@atrium/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAppConfig } from "../../lib/config";
import { RequestInvite } from "../marketing/RequestInvite";
import { ApiError, api, errorMessage } from "../../lib/api";
import { Button } from "../../components/ui/Button";
import { Field, Input } from "../../components/ui/Input";
import { LogoMark } from "../../components/Logo";
import { PageSpinner } from "../../components/ui/Spinner";
import { useAuth } from "./AuthProvider";
import { rememberSignInPassword } from "../../lib/e2ee/session";

/** Server routes that finish a sign-in elsewhere (the developer portal) and need a real page load. */
const FULL_LOAD_PATHS = ["/api/auth/handoff"];

/**
 * Where to go after signing in. Only same-origin paths are allowed, so `next`
 * can never send someone to another site. App routes navigate client-side;
 * the portal sign-in handoff is a server route, so it gets a full page load.
 */
export function nextTarget(next: string | null, fallback = "/feed"): { path: string; full: boolean } {
  const none = { path: fallback, full: false };
  if (!next || !next.startsWith("/") || next.startsWith("//") || next.startsWith("/\\")) return none;
  let u: URL;
  try {
    u = new URL(next, window.location.origin);
  } catch {
    return none;
  }
  if (u.origin !== window.location.origin) return none;
  const path = u.pathname + u.search + u.hash;
  if (FULL_LOAD_PATHS.includes(u.pathname)) return { path, full: true };
  if (u.pathname.startsWith("/api/") || u.pathname.startsWith("/media/")) return none;
  return { path, full: false };
}

function goNext(navigate: NavigateFunction, next: string | null, fallback?: string) {
  const t = nextTarget(next, fallback);
  // Full loads are handled by <Continue> once the session is set.
  if (!t.full) navigate(t.path, { replace: true });
}

/** The full-page target already being loaded, so re-renders don't start (and abort) it twice. */
let leavingFor: string | null = null;

/** Rendered once signed in: carry on to `next`. */
function Continue({ next }: { next: string | null }) {
  const t = nextTarget(next);
  useEffect(() => {
    if (!t.full || leavingFor === t.path) return;
    leavingFor = t.path;
    window.location.replace(t.path);
  }, [t.full, t.path]);
  return t.full ? <PageSpinner /> : <Navigate to={t.path} replace />;
}

function AuthLayout({ title, intro, children, footer }: { title: string; intro?: ReactNode; children: ReactNode; footer?: ReactNode }) {
  useEffect(() => {
    document.title = `${title} · Atrium`;
    return () => void (document.title = "Atrium");
  }, [title]);
  return (
    <div className="flex min-h-full flex-col items-center justify-center bg-bg px-5 py-12">
      <div className="w-full max-w-[360px] animate-slide-up">
        <Link to="/" aria-label="Atrium home" className="mb-6 inline-block rounded-md">
          <LogoMark className="size-9 text-fg" />
        </Link>
        <h1 className={intro ? "mb-2 text-2xl font-semibold tracking-tight text-fg" : "mb-6 text-2xl font-semibold tracking-tight text-fg"}>{title}</h1>
        {intro && <p className="mb-6 text-base text-fg-2">{intro}</p>}
        {children}
        {footer && <p className="mt-6 text-sm text-fg-3">{footer}</p>}
      </div>
    </div>
  );
}

function FormError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p role="alert" className="rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
      {message}
    </p>
  );
}

/** Carry `next` between the sign-in and sign-up pages. */
const withNext = (path: string, next: string | null) => (next ? `${path}?next=${encodeURIComponent(next)}` : path);

function zodFields(issues: { path: PropertyKey[]; message: string }[]) {
  const out: Record<string, string> = {};
  for (const i of issues) {
    const k = String(i.path[0] ?? "_");
    out[k] ??= i.message;
  }
  return out;
}

export function LoginPage() {
  const { me, setMe } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ identifier: "", password: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);
  /** Set when the server wants the code it just emailed (signing in from somewhere new). */
  const [codeSentTo, setCodeSentTo] = useState<string | null>(null);

  if (me) return <Continue next={params.get("next")} />;

  /** Sign in with the typed credentials (and the emailed code, once asked for). Throws ApiError. */
  const signIn = async (code?: string) => {
    const res = await api.post<Me | SignInCodeRequired>("/api/auth/login", { ...form, identifier: form.identifier.trim(), code });
    if ("codeRequired" in res) return setCodeSentTo(res.email);
    // Encrypted messages are on by default: signing in creates or unlocks this device's keys.
    rememberSignInPassword(form.password);
    setMe(res);
    goNext(navigate, params.get("next"));
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = loginSchema.safeParse(form);
    if (!parsed.success) return setErrors(zodFields(parsed.error.issues));
    setPending(true);
    setErrors({});
    try {
      await signIn();
    } catch (err) {
      setErrors({ _: err instanceof ApiError ? err.message : "Couldn't sign in. Try again." });
    } finally {
      setPending(false);
    }
  };

  if (codeSentTo) return <SignInCodeStep email={codeSentTo} signIn={signIn} back={() => setCodeSentTo(null)} />;

  return (
    <AuthLayout
      title="Sign in to Atrium"
      footer={
        <>
          New here?{" "}
          <Link to={withNext("/sign-up", params.get("next"))} className="font-medium text-fg hover:underline">
            Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Field label="Username or email" error={errors.identifier}>
          {(p) => <Input {...p} autoComplete="username" autoFocus value={form.identifier} onChange={(e) => setForm({ ...form, identifier: e.target.value })} />}
        </Field>
        <Field label="Password" error={errors.password}>
          {(p) => <Input {...p} type="password" autoComplete="current-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />}
        </Field>
        <p className="-mt-1 text-sm">
          <Link to="/resetpassword" className="text-fg-2 hover:text-fg hover:underline">
            Forgot your password?
          </Link>
        </p>
        <FormError message={errors._} />
        <Button type="submit" variant="primary" size="lg" className="w-full" loading={pending}>
          Sign in
        </Button>
      </form>
    </AuthLayout>
  );
}

const RESEND_COOLDOWN_S = 30;

/** Second step when signing in from a network and browser the account hasn't used: the 6-digit code emailed to it. */
function SignInCodeStep({ email, signIn, back }: { email: string; signIn: (code?: string) => Promise<void>; back: () => void }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState<{ code?: string; form?: string }>({});
  const [pending, setPending] = useState(false);
  const [resent, setResent] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_S);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const showError = (err: unknown) =>
    setError(err instanceof ApiError ? (err.fields?.code ? { code: err.fields.code } : { form: err.message }) : { form: "Couldn't sign in. Try again." });

  const verify = async (value: string) => {
    if (!/^\d{6}$/.test(value)) return setError({ code: "Enter the 6-digit code from the email" });
    setPending(true);
    setError({});
    try {
      await signIn(value);
    } catch (err) {
      showError(err);
    } finally {
      setPending(false);
    }
  };

  const resend = async () => {
    setError({});
    setResent(false);
    try {
      await signIn();
      setCode("");
      setResent(true);
      setCooldown(RESEND_COOLDOWN_S);
    } catch (err) {
      showError(err);
    }
  };

  return (
    <AuthLayout
      title="Check your email"
      intro={
        <>
          You're signing in from somewhere new, so we sent a 6-digit code to <span className="font-medium text-fg">{email}</span>. It expires in 10 minutes.
        </>
      }
      footer={
        <button type="button" onClick={back} className="font-medium text-fg hover:underline">
          Use a different account
        </button>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void verify(code);
        }}
        className="space-y-4"
        noValidate
      >
        <Field label="Code" error={error.code}>
          {(p) => (
            <Input
              {...p}
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              className="font-mono text-lg tracking-[0.4em]"
              value={code}
              onChange={(e) => {
                const next = e.target.value.replace(/\D/g, "").slice(0, 6);
                setCode(next);
                // Typed or pasted the whole code: no need to press the button.
                if (next.length === 6 && code.length < 6 && !pending) void verify(next);
              }}
            />
          )}
        </Field>
        {resent && !error.form && <p className="text-sm text-fg-2">We sent a new code. Only the newest one works.</p>}
        <FormError message={error.form} />
        <Button type="submit" variant="primary" size="lg" className="w-full" loading={pending}>
          Sign in
        </Button>
        <p className="text-sm text-fg-2">
          Didn't get it? Check your spam folder, or{" "}
          {cooldown > 0 ? (
            <span className="text-fg-3">send a new code in {cooldown}s</span>
          ) : (
            <button type="button" onClick={resend} className="font-medium text-fg hover:underline">
              send a new code
            </button>
          )}
          .
        </p>
      </form>
    </AuthLayout>
  );
}

/**
 * /sign-up. While Atrium is invite-only, it needs the link from an invite email
 * (?invite=…); the account takes the invited address. Without one it offers a
 * place on the waitlist instead.
 */
export function RegisterPage() {
  const { me } = useAuth();
  const [params] = useSearchParams();
  const config = useAppConfig();
  const invite = params.get("invite");

  if (me) return <Continue next={params.get("next")} />;
  if (config.isPending) return <PageSpinner />;
  if (invite) return <InvitedSignUp token={invite} />;
  if (config.data?.inviteOnly !== false) return <InviteOnly />;
  return <SignUpForm />;
}

function InviteOnly() {
  const [params] = useSearchParams();
  return (
    <AuthLayout
      title="Atrium is invite-only for now"
      intro="We're letting people in a few at a time. Leave your email and we'll send you an invite when there's room."
      footer={
        <>
          Already have an account?{" "}
          <Link to={withNext("/login", params.get("next"))} className="font-medium text-fg hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      <RequestInvite />
      <p className="mt-4 text-sm text-fg-3">Got an invite? Use the link in the email. It's tied to the address it was sent to.</p>
    </AuthLayout>
  );
}

function InvitedSignUp({ token }: { token: string }) {
  const q = useQuery({
    queryKey: ["invite", token],
    queryFn: () => api.get<{ email: string }>(`/api/access/invite?token=${encodeURIComponent(token)}`),
    retry: false,
    staleTime: Infinity,
  });
  if (q.isPending) return <PageSpinner />;
  if (q.isError)
    return (
      <AuthLayout
        title="This invite can't be used"
        intro={q.error instanceof ApiError && q.error.status === 404 ? "It has expired or has already been used. Ask whoever invited you to send a new one." : errorMessage(q.error)}
        footer={
          <>
            Already signed up?{" "}
            <Link to="/login" className="font-medium text-fg hover:underline">
              Sign in
            </Link>
          </>
        }
      >
        <Link to="/" className="inline-flex h-10 w-full items-center justify-center rounded-md bg-surface-2 text-base font-medium text-fg hover:bg-surface-3">
          Go to the home page
        </Link>
      </AuthLayout>
    );
  return <SignUpForm invite={{ token, email: q.data.email }} />;
}

function SignUpForm({ invite }: { invite?: { token: string; email: string } }) {
  const { setMe } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ displayName: "", username: "", email: invite?.email ?? "", password: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [k]: e.target.value });
    if (errors[k]) setErrors({ ...errors, [k]: "" });
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = registerSchema.safeParse({ ...form, ...(invite ? { inviteToken: invite.token } : {}) });
    if (!parsed.success) return setErrors(zodFields(parsed.error.issues));
    // Same policy the server enforces; checked here so the message is instant.
    const verdict = checkUsername(parsed.data.username);
    if (!verdict.ok) return setErrors({ username: verdict.message ?? "That username isn't available" });
    setPending(true);
    setErrors({});
    try {
      const user = await api.post<Me>("/api/auth/register", parsed.data);
      // Creates the new account's encryption keys.
      rememberSignInPassword(parsed.data.password);
      setMe(user);
      goNext(navigate, params.get("next"), "/explore?welcome=1");
    } catch (err) {
      if (err instanceof ApiError && err.fields) setErrors(err.fields);
      else setErrors({ _: err instanceof ApiError ? err.message : "Couldn't create your account. Try again." });
    } finally {
      setPending(false);
    }
  };

  return (
    <AuthLayout
      title={invite ? "You're invited" : "Create your account"}
      intro={invite ? "Pick a name and a username to finish setting up your account." : undefined}
      footer={
        <>
          Already have an account?{" "}
          <Link to={withNext("/login", params.get("next"))} className="font-medium text-fg hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Field label="Name" error={errors.displayName}>
          {(p) => <Input {...p} autoComplete="name" autoFocus value={form.displayName} onChange={set("displayName")} maxLength={50} />}
        </Field>
        <Field label="Username" error={errors.username} hint="Letters, numbers and underscores">
          {(p) => (
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-fg-3">@</span>
              <Input {...p} className="pl-7" autoComplete="username" value={form.username} onChange={set("username")} maxLength={24} spellCheck={false} />
            </div>
          )}
        </Field>
        <Field label="Email" error={errors.email} hint={invite ? "Your invite is for this address" : undefined}>
          {(p) => <Input {...p} type="email" autoComplete="email" value={form.email} onChange={set("email")} readOnly={!!invite} className={invite ? "text-fg-2" : undefined} />}
        </Field>
        <Field label="Password" error={errors.password} hint="At least 8 characters">
          {(p) => <Input {...p} type="password" autoComplete="new-password" value={form.password} onChange={set("password")} />}
        </Field>
        <FormError message={errors._} />
        <Button type="submit" variant="primary" size="lg" className="w-full" loading={pending}>
          Create account
        </Button>
        <p className="text-xs leading-relaxed text-fg-3">
          By creating an account, you agree to Atrium's{" "}
          <Link to="/terms" target="_blank" className="font-medium text-fg-2 hover:text-fg hover:underline">
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link to="/privacy" target="_blank" className="font-medium text-fg-2 hover:text-fg hover:underline">
            Privacy Policy
          </Link>
          .{!invite && " We'll email you a link to confirm your address."}
        </p>
      </form>
    </AuthLayout>
  );
}

/**
 * /resetpassword: ask for a reset link, or (with ?token= from the email) choose
 * a new password. The request always reports success, so it can't be used to
 * find out who has an account.
 */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  return token ? <ChooseNewPassword token={token} /> : <RequestReset />;
}

function RequestReset() {
  const { me } = useAuth();
  const [email, setEmail] = useState(me?.email ?? "");
  const [error, setError] = useState<string>();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = forgotPasswordSchema.safeParse({ email });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message);
    setPending(true);
    setError(undefined);
    try {
      await api.post("/api/auth/password/forgot", parsed.data);
      setSentTo(parsed.data.email);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't send the email. Try again.");
    } finally {
      setPending(false);
    }
  };

  if (sentTo)
    return (
      <AuthLayout
        title="Check your inbox"
        intro={
          <>
            If there's an account for <span className="font-medium text-fg">{sentTo}</span>, we've sent it a link to reset the password. The link expires in an hour.
          </>
        }
        footer={
          <>
            Didn't get it? Check your spam folder or{" "}
            <button type="button" className="font-medium text-fg hover:underline" onClick={() => setSentTo(null)}>
              try again
            </button>
            .
          </>
        }
      >
        <Link to="/login" className="inline-flex h-10 w-full items-center justify-center rounded-md bg-surface-2 text-base font-medium text-fg hover:bg-surface-3">
          Back to sign in
        </Link>
      </AuthLayout>
    );

  return (
    <AuthLayout
      title="Reset your password"
      intro="Enter the email address on your account and we'll send you a link to choose a new password."
      footer={
        <>
          Remembered it?{" "}
          <Link to="/login" className="font-medium text-fg hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Field label="Email" error={error}>
          {(p) => <Input {...p} type="email" autoComplete="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />}
        </Field>
        <Button type="submit" variant="primary" size="lg" className="w-full" loading={pending}>
          Send reset link
        </Button>
      </form>
    </AuthLayout>
  );
}

function ChooseNewPassword({ token }: { token: string }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [done, setDone] = useState(false);
  const [pending, setPending] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = passwordSchema.safeParse(form.password);
    if (!parsed.success) return setErrors({ password: parsed.error.issues[0]?.message ?? "Choose a different password" });
    if (form.password !== form.confirm) return setErrors({ confirm: "The passwords don't match" });
    setPending(true);
    setErrors({});
    try {
      await api.post("/api/auth/password/reset", { token, newPassword: form.password });
      // Every session for the account has ended, including this browser's.
      qc.setQueryData(["me"], null);
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError && err.fields?.newPassword) setErrors({ password: err.fields.newPassword });
      else setErrors({ _: err instanceof ApiError ? err.message : "Couldn't reset your password. Try again." });
    } finally {
      setPending(false);
    }
  };

  if (done)
    return (
      <AuthLayout title="Password changed" intro="You've been signed out everywhere. Sign in with your new password.">
        <Link to="/login" replace className="inline-flex h-10 w-full items-center justify-center rounded-md bg-primary text-base font-medium text-fg-inverse hover:bg-primary-hover">
          Sign in
        </Link>
      </AuthLayout>
    );

  const expired = errors._ && /expired|used/i.test(errors._);
  return (
    <AuthLayout
      title="Choose a new password"
      intro="You'll be signed out on every device once it's changed."
      footer={
        expired ? (
          <Link to="/resetpassword" className="font-medium text-fg hover:underline">
            Send a new link
          </Link>
        ) : undefined
      }
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Field label="New password" error={errors.password} hint="At least 8 characters">
          {(p) => <Input {...p} type="password" autoComplete="new-password" autoFocus value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />}
        </Field>
        <Field label="Confirm new password" error={errors.confirm}>
          {(p) => <Input {...p} type="password" autoComplete="new-password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} />}
        </Field>
        <FormError message={errors._} />
        <Button type="submit" variant="primary" size="lg" className="w-full" loading={pending}>
          Change password
        </Button>
      </form>
    </AuthLayout>
  );
}

/** Where the confirmation link lands after the server has checked it. */
export function VerifyEmailPage() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const ok = params.get("status") === "verified";
  const [resent, setResent] = useState<"idle" | "sending" | "sent" | "error">("idle");

  useEffect(() => {
    if (ok) void qc.invalidateQueries({ queryKey: ["me"] });
  }, [ok, qc]);

  const resend = async () => {
    setResent("sending");
    try {
      await api.post("/api/auth/verify-email/resend");
      setResent("sent");
    } catch {
      setResent("error");
    }
  };

  const continueLink = (
    <Link to={me ? "/feed" : "/login"} replace className="inline-flex h-10 w-full items-center justify-center rounded-md bg-primary text-base font-medium text-fg-inverse hover:bg-primary-hover">
      {me ? "Continue to Atrium" : "Sign in"}
    </Link>
  );

  if (ok) return <AuthLayout title="Email confirmed" intro="Thanks! Your email address is confirmed.">{continueLink}</AuthLayout>;

  return (
    <AuthLayout title="That link didn't work" intro="It may have expired or been copied incompletely. Confirmation links last 24 hours.">
      <div className="space-y-3">
        {me ? (
          <Button variant="primary" size="lg" className="w-full" loading={resent === "sending"} disabled={resent === "sent"} onClick={resend}>
            {resent === "sent" ? `Sent to ${me.email}` : "Send a new link"}
          </Button>
        ) : (
          continueLink
        )}
        {resent === "error" && <FormError message="Couldn't send a new link. Try again in a few minutes." />}
        {!me && <p className="text-sm text-fg-3">Sign in to get a new confirmation link.</p>}
      </div>
    </AuthLayout>
  );
}
